// src/Footer.js
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SITE_CONFIG } from './config';
import { useTheme } from './App';

function Footer() {
  const { theme, isDarkMode } = useTheme();

  // Build social icons array based on config
  const socialIcons = [];
  if (SITE_CONFIG.display.footer.xIcon) {
    socialIcons.push({
      href: SITE_CONFIG.social.twitter,
      icon: isDarkMode ? "/x_white_logo.png" : "/x_logo.png",
      alt: "X"
    });
  }
  if (SITE_CONFIG.display.footer.discordIcon) {
    socialIcons.push({
      href: SITE_CONFIG.social.discord,
      icon: isDarkMode ? "/Discord-Symbol-White.png" : "/Discord-Symbol-Black.png",
      alt: "Discord"
    });
  }

  return (
    <footer className={`${theme.primary.footer} p-8 text-center md:text-left border-t shadow-2xl`}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="flex flex-col md:flex-row justify-between items-start space-y-8 md:space-y-0"
      >
        {/* Left Column: Brand and Description - Hidden on mobile */}
        <div className="hidden md:block w-full md:w-1/4 text-center md:text-left">
          <div className="flex items-center space-x-3 mb-4">
            <img
              src={isDarkMode ? "/black_logo.png" : "/white_logo.png"}
              alt={SITE_CONFIG.brandName}
              className="w-10 h-10 object-contain rounded-lg shadow-lg"
            />
            <h3 className={`text-2xl font-bold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent`}>
              {SITE_CONFIG.brandName}
            </h3>
          </div>
          <p className={`${theme.primary.textSecondary} text-sm leading-relaxed`}>
            Your trusted shopping companion for discovering amazing deals and savings. We monitor thousands of products daily to bring you the best offers from top retailers.
          </p>
        </div>

        {/* Right Columns: Navigation and More */}
        <div className="w-full md:w-1/3 flex flex-col md:flex-row justify-end items-start space-y-8 md:space-y-0 md:space-x-8">
          {/* Navigation - Hidden on mobile */}
          <div className="hidden md:block w-full md:w-1/2 text-center md:text-left">
            <h4 className={`text-lg font-semibold ${theme.primary.text} mb-4 bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent`}>Navigation</h4>
            <ul className="space-y-3">
              {[
                { to: '/', label: 'Home' },
                { to: '/deals', label: 'Deals' },
                { to: '/faq', label: 'FAQ' }
              ].map((link, index) => (
                <motion.li key={link.to} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link to={link.to} className={`${theme.primary.textSecondary} hover:bg-gradient-to-r hover:${theme.primary.accent} hover:bg-clip-text hover:text-transparent block transition-colors`}>
                    {link.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* More */}
          <div className="w-full md:w-1/2 text-center md:text-left">
            <h4 className={`text-lg font-semibold ${theme.primary.text} mb-4 bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent`}>More</h4>
            <ul className="space-y-3">
              {[
                { to: '/contact', label: 'Contact Us' },
                { to: '/privacy', label: 'Privacy Policy' },
                { to: '/terms', label: 'Terms & Conditions' },
                { to: '/cookies', label: 'Cookie Policy' }
              ].map((link, index) => (
                <motion.li key={link.to} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link to={link.to} className={`${theme.primary.textSecondary} hover:bg-gradient-to-r hover:${theme.primary.accent} hover:bg-clip-text hover:text-transparent block transition-colors`}>
                    {link.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>

        {/* Affiliate Disclosure */}
        <div className="w-full md:w-1/4 text-center md:text-left">
          <h4 className={`text-lg font-semibold ${theme.primary.text} mb-4 bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent`}>Affiliate Disclosure</h4>
          <p className={`${theme.primary.textSecondary} text-sm leading-relaxed`}>
            As an Amazon Associate I earn from qualifying purchases. This platform also participates in additional affiliate programs and may earn commissions from qualifying purchases made through our referral links.
          </p>
        </div>

        {/* Right Column: Social Icons - Only render if any icons are enabled */}
        {socialIcons.length > 0 && (
          <div className="w-full md:w-1/4 flex justify-center md:justify-end items-center space-x-6">
            {socialIcons.map((social, index) => (
              <motion.a
                key={social.alt}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.95 }}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`${theme.primary.text} hover:bg-gradient-to-r hover:${theme.primary.accent} hover:bg-clip-text hover:text-transparent transition-colors ${theme.primary.card} p-2 rounded-lg border shadow-lg`}
              >
                <img src={social.icon} alt={social.alt} className="w-8 h-8 object-contain" />
              </motion.a>
            ))}
          </div>
        )}
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
        className="mt-8 pt-6 border-t border-gray-200/20"
      >
        <p className={`${theme.primary.textSecondary} text-sm text-center`}>
          All Rights Reserved. {SITE_CONFIG.brandName}
        </p>
      </motion.div>
    </footer>
  );
}

export default Footer;
